// Proportional monetization/visibility guardrails -- mirrors
// backend_stocker/app/utils/monetization/monetization_guardrails.py exactly
// (same 4 states, same thresholds, same price ceilings). Display-only here;
// every enforcement decision is re-checked server-side regardless of what
// this file computes (set_group_pricing / create_group / update_group all
// re-validate independently) -- this file exists so the UI can explain the
// system and show progress without an extra round-trip, not as the source
// of truth for what's actually allowed.
//
// Built 2026-07-19, reusing the existing, already-verified credibility tier
// system (see credibilityTiers.js) rather than a new score -- an earlier,
// externally-authored "Composite Trust Score" proposal assumed an 850-point
// scale that doesn't exist in the real formula, and both of its own worked
// examples failed when Monte Carlo-tested against the real code (see
// Technical_Docs/29_CREDENTIAL_ASSESSMENT_REGULATORY_REVIEW.md §6).

export const GUARDRAIL_STATES = {
  Beginner: "sandbox",
  Active: "sandbox",
  Advanced: "guarded",
  Elite: "extended",
  "Top Emperor": "unrestricted",
};

export const GUARDRAIL_ORDER = ["sandbox", "guarded", "extended", "unrestricted"];

export const GUARDRAIL_INFO = {
  sandbox: {
    label: "Sandbox",
    minScore: 0,
    priceCeilingCents: 0,
    allowsPublicGroup: false,
    description: "Build your track record for free. Groups stay private (invite/link-only) and monetization isn't available yet.",
  },
  guarded: {
    label: "Guarded",
    minScore: 650,
    priceCeilingCents: 999,
    allowsPublicGroup: false,
    description: "Monetization unlocked, capped at €9.99/period. Groups still stay private while your track record keeps building.",
  },
  extended: {
    label: "Extended",
    minScore: 800,
    priceCeilingCents: 4999,
    allowsPublicGroup: true,
    description: "Higher pricing ceiling (€49.99/period) and your group can go public, discoverable in search and browse.",
  },
  unrestricted: {
    label: "Unrestricted",
    minScore: 900,
    priceCeilingCents: null,
    allowsPublicGroup: true,
    description: "No price ceiling. Full public discoverability. The platform's strongest, most-verified track records.",
  },
};

// GUARDRAIL_INFO's own label/description stay English -- they're the
// fallback `defaultValue` these look up against, and the internal `state`
// keys ("sandbox"/"guarded"/...) are used for lookups elsewhere in this
// file, not for display. Callers rendering user-facing text should use
// these instead of `info.label`/`info.description` directly.
export const translateGuardrailLabel = (state, t) =>
  t ? t(`credibility.guardrailTiers.${state}.label`, { defaultValue: GUARDRAIL_INFO[state]?.label }) : GUARDRAIL_INFO[state]?.label;

export const translateGuardrailDescription = (state, t) =>
  t ? t(`credibility.guardrailTiers.${state}.description`, { defaultValue: GUARDRAIL_INFO[state]?.description }) : GUARDRAIL_INFO[state]?.description;

export const guardrailStateForTier = (statusTier) => GUARDRAIL_STATES[statusTier] || "sandbox";

export const guardrailStatus = (statusTier, globalScore = 0) => {
  const state = guardrailStateForTier(statusTier);
  const idx = GUARDRAIL_ORDER.indexOf(state);
  const nextState = idx + 1 < GUARDRAIL_ORDER.length ? GUARDRAIL_ORDER[idx + 1] : null;
  const info = GUARDRAIL_INFO[state];
  const nextInfo = nextState ? GUARDRAIL_INFO[nextState] : null;

  return {
    state,
    ...info,
    nextState,
    nextStateLabel: nextInfo?.label ?? null,
    nextStateMinScore: nextInfo?.minScore ?? null,
    pointsToNextState: nextInfo ? Math.max(0, Math.round((nextInfo.minScore - globalScore) * 100) / 100) : 0,
  };
};

export const formatPriceCeiling = (cents, t) => {
  if (cents === null || cents === undefined) return t ? t('credibility.guardrailPanel.noCap') : "No cap";
  if (cents === 0) return t ? t('credibility.guardrailPanel.notAvailable') : "Not available";
  return `€${(cents / 100).toFixed(2)}`;
};
